import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { cn } from "@/lib/utils";

type NavLinkItem = { to: string; label: string };
type NavSeparatorItem = { type: "separator" };
type NavItem = NavLinkItem | NavSeparatorItem;
type NavSection = { label?: string; items: NavItem[] };

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { to: "/", label: "ダッシュボード" },
      { to: "/reports/cushion-performance", label: "レポート" },
    ],
  },
  {
    label: "クッション",
    items: [
      { to: "/nova-sites", label: "クッション広告" },
      { to: "/ctas", label: "CTA" },
      { to: "/lps", label: "LPテンプレート" },
      { to: "/media", label: "ファイル" },
    ],
  },
  {
    label: "ダイレクト",
    items: [
      { to: "/offers", label: "ダイレクト広告" },
      { to: "/materials", label: "ダイレクト素材" },
    ],
  },
  {
    label: "運用",
    items: [
      { to: "/partnership-requests", label: "提携申請" },
      { to: "/users", label: "運用者" },
    ],
  },
  {
    label: "請求",
    items: [
      { to: "/invoices", label: "請求書" },
      { to: "/payments", label: "支払" },
    ],
  },
  {
    label: "ログ",
    items: [
      { to: "/access", label: "アクセス" },
      { to: "/cta-access", label: "CTAクリック" },
      { to: "/results", label: "成果" },
      { to: "/postbacks", label: "ポストバック" },
      { type: "separator" },
      { to: "/spark-sync", label: "SPARK同期" },
      { to: "/mail-deliveries", label: "メール送信" },
      { to: "/stop-reservations", label: "停止予約" },
    ],
  },
  {
    items: [{ to: "/settings", label: "設定" }],
  },
];

function isNavLinkItem(item: NavItem): item is NavLinkItem {
  return !("type" in item);
}

function isActiveNavItem(item: NavLinkItem, pathname: string): boolean {
  if (item.to === "/") return pathname === "/";
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function activeSectionLabels(pathname: string): string[] {
  return NAV_SECTIONS.flatMap((section) =>
    section.label &&
    section.items.some(
      (item) => isNavLinkItem(item) && isActiveNavItem(item, pathname),
    )
      ? [section.label]
      : [],
  );
}

export function AdminShell() {
  const { state, logout } = useAuthContext();
  const location = useLocation();
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(
    () => new Set(activeSectionLabels(location.pathname)),
  );
  const activeLabels = useMemo(
    () => activeSectionLabels(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (activeLabels.length === 0) return;
    setOpenSections((current) => {
      const next = new Set(current);
      activeLabels.forEach((label) => next.add(label));
      return next;
    });
  }, [activeLabels]);

  if (state.status !== "authenticated") return null;
  const user = state.user;
  const userLabel = user.displayName ?? user.email ?? user.id;
  const toggleSection = (label: string) => {
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="flex h-screen">
      <aside className="flex w-60 shrink-0 flex-col bg-neutral-900 text-white">
        <div className="flex h-16 shrink-0 items-center border-b border-neutral-800 px-6">
          <img src="/nova-logo.png" alt="NOVA 管理画面" className="h-10 w-auto" />
        </div>
        <nav className="flex flex-1 flex-col gap-4 overflow-y-auto px-3 py-4">
          {NAV_SECTIONS.map((section, sectionIndex) => {
            const sectionKey = section.label ?? `direct-${sectionIndex}`;
            const isCollapsible = Boolean(section.label);
            const isOpen = section.label
              ? openSections.has(section.label)
              : true;
            const contentId = `sidebar-section-${sectionKey}`;

            return (
              <div key={sectionKey}>
                {section.label ? (
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    onClick={() => toggleSection(section.label!)}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-semibold text-neutral-400 hover:bg-neutral-800 hover:text-white"
                  >
                    <span>{section.label}</span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform",
                        !isOpen && "-rotate-90",
                      )}
                    />
                  </button>
                ) : null}
                {isOpen ? (
                  <div
                    id={contentId}
                    className={cn(
                      "flex flex-col gap-1",
                      isCollapsible && "mt-1",
                    )}
                  >
                    {section.items.map((item, itemIndex) =>
                      "type" in item ? (
                        <div
                          key={`separator-${itemIndex}`}
                          className="my-2 border-t border-neutral-800"
                        />
                      ) : (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.to === "/"}
                          className={({ isActive }) =>
                            cn(
                              "rounded-md px-3 py-2 text-sm text-neutral-300 hover:bg-neutral-800 hover:text-white",
                              isCollapsible && "pl-5",
                              isActive && "bg-neutral-800 text-white",
                            )
                          }
                        >
                          {item.label}
                        </NavLink>
                      ),
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col bg-neutral-50">
        <header className="flex h-16 shrink-0 items-center justify-end border-b border-neutral-800 bg-neutral-900 px-6 text-white">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="max-w-72 justify-start gap-2 px-3 text-neutral-300 hover:bg-neutral-800 hover:text-white"
              >
                <User className="h-4 w-4 text-neutral-400" />
                <span className="truncate">{userLabel}</span>
                <ChevronDown className="h-4 w-4 text-neutral-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="space-y-1">
                <div className="truncate font-medium">{userLabel}</div>
                <div className="truncate text-xs font-normal text-muted-foreground">
                  {user.email ?? user.id}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => {
                  void logout();
                }}
              >
                <LogOut className="h-4 w-4" />
                ログアウト
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[1600px] px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
